import { createContext, useContext, useState, useCallback } from "react";
import { INITIAL_PRODUCTS, MOCK_NOTIFICATIONS } from "../data/mockData";

const AppContext = createContext(null);

// Deep clone helper to isolate per-tenant data
const cloneData = (data) => JSON.parse(JSON.stringify(data));

export function AppProvider({ children }) {
  const [products, setProducts] = useState(cloneData(INITIAL_PRODUCTS));
  const [notifications, setNotifications] = useState(
    cloneData(MOCK_NOTIFICATIONS),
  );

  // ── Products ─────────────────────────────────────────────────────────────
  const getProducts = useCallback(
    (userId) => products[userId] || [],
    [products],
  );

  const addProduct = useCallback((userId, product) => {
    setProducts((prev) => {
      const list = prev[userId] || [];
      const newId =
        list.length > 0 ? Math.max(...list.map((p) => p.id)) + 1 : 1;
      return {
        ...prev,
        [userId]: [...list, { ...product, id: newId, views: 0, sales: 0 }],
      };
    });
  }, []);

  const updateProduct = useCallback((userId, updatedProduct) => {
    setProducts((prev) => ({
      ...prev,
      [userId]: (prev[userId] || []).map((p) =>
        p.id === updatedProduct.id ? updatedProduct : p,
      ),
    }));
  }, []);

  const deleteProduct = useCallback((userId, productId) => {
    setProducts((prev) => ({
      ...prev,
      [userId]: (prev[userId] || []).filter((p) => p.id !== productId),
    }));
  }, []);

  // ── Notifications ─────────────────────────────────────────────────────────
  const getNotifications = useCallback(
    (userId) => notifications[userId] || [],
    [notifications],
  );

  const markNotificationRead = useCallback((userId, notifId) => {
    setNotifications((prev) => ({
      ...prev,
      [userId]: (prev[userId] || []).map((n) =>
        n.id === notifId ? { ...n, read: true } : n,
      ),
    }));
  }, []);

  const markAllRead = useCallback((userId) => {
    setNotifications((prev) => ({
      ...prev,
      [userId]: (prev[userId] || []).map((n) => ({ ...n, read: true })),
    }));
  }, []);

  const unreadCount = useCallback(
    (userId) => (notifications[userId] || []).filter((n) => !n.read).length,
    [notifications],
  );

  return (
    <AppContext.Provider
      value={{
        getProducts,
        addProduct,
        updateProduct,
        deleteProduct,
        getNotifications,
        markNotificationRead,
        markAllRead,
        unreadCount,
      }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
